import React, { useState, useRef } from "react";
import {
  Avatar,
  Box,
  ClickAwayListener,
  Paper,
  MenuItem,
  Button,
  Typography,
  Divider,
  useTheme,
  Chip,
  ListItemIcon,
  ListItemText} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  KeyboardArrowDown,
  KeyboardArrowUp} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  HOME_ROUTE_PATH,
  LOGIN_ROUTE_PATH,
  USER_PROFILE_ROUTE_PATH} from "../../utils/constants/AppConstants";
import { useAuth } from "../../auth/AuthContext";
import { useUserProfileContext } from "../../contexts/ProfileContext";

const LoggedInProfileMenuComponent: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const menuRef = useRef<HTMLButtonElement>(null);

  const { userProfile } = useUserProfileContext();

  const userName = userProfile?.name || 'User';
  const firstName = userName.split(' ')[0]; // Extract first name
  
  // Truncate long first names for display (show max 20 chars)
  const displayName = firstName.length > 20 ? firstName.substring(0, 20) + '...' : firstName;

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const toProfilePage = () => {
    navigate(USER_PROFILE_ROUTE_PATH);
    handleClose();
  };

  const toDashboard = () => {
    navigate(HOME_ROUTE_PATH);
    handleClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(LOGIN_ROUTE_PATH);
      handleClose();
    } catch (error) {
      console.error('Logout failed:', error);
      // Still navigate to login even if logout fails
      navigate(LOGIN_ROUTE_PATH);
      handleClose();
    }
  };

  const menuItems = [
    {
      icon: <DashboardIcon />,
      text: "Dashboard",
      action: toDashboard,
      color: theme.palette.primary.main},
    {
      icon: <PersonIcon />,
      text: "Profile",
      action: toProfilePage,
      color: theme.palette.info.main},
    {
      icon: <LogoutIcon />,
      text: "Sign Out",
      action: handleLogout,
      color: theme.palette.error.main,
      divider: true},
  ];

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: "relative", display: "inline-block" }}>
        {/* Profile Button */}
        <Button
          ref={menuRef}
          onClick={handleToggle}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          startIcon={
            <Avatar
              sx={{
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                fontWeight: 600}}
            >
              {userName.charAt(0).toUpperCase()}
            </Avatar>
          }
          endIcon={isOpen ? <KeyboardArrowUp sx={{ display: { xs: 'none', sm: 'block' } }} /> : <KeyboardArrowDown sx={{ display: { xs: 'none', sm: 'block' } }} />}
          sx={{
            borderRadius: 3,
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.5, sm: 1 },
            backgroundColor: isHovered || isOpen ? theme.palette.primary.light + '10' : 'transparent',
            color: theme.palette.text.primary,
            fontWeight: 500,
            textTransform: 'none',
            border: `1px solid ${theme.palette.divider}`,
            minWidth: { xs: 100, sm: 140 },
            justifyContent: 'space-between',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: theme.palette.primary.light + '15',
              borderColor: theme.palette.primary.main,
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${theme.palette.primary.main}20`}}}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ml: { xs: 0.5, sm: 1 }, overflow: 'hidden', flex: 1 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600, 
                lineHeight: 1.2,
                maxWidth: { xs: '60px', sm: '120px' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
              title={userName} // Tooltip shows full name on hover
            >
              {displayName}
            </Typography>
            <Chip
              label="Online"
              size="small"
              sx={{
                display: { xs: 'none', sm: 'flex' },
                height: 16,
                fontSize: '0.6rem',
                backgroundColor: theme.palette.success.light + '30',
                color: theme.palette.success.dark,
                '& .MuiChip-label': {
                  px: 1}}}
            />
          </Box>
        </Button>

        {/* Dropdown Menu */}
        {isOpen && (
          <Paper
            sx={{
              position: "absolute",
              top: "calc(100% + 8px)",
              right: 0,
              minWidth: 220,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
              zIndex: 1000,
              background: theme.palette.background.paper,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'}}
          >
            {/* User info header */}
            <Box sx={{ p: 3, pb: 2, backgroundColor: theme.palette.grey[50] }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    fontSize: '1.2rem',
                    fontWeight: 700}}
                >
                  {userName.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={userName}
                  >
                    {displayName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Account Holder
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* Menu items */}
            {menuItems.map((item, index) => (
              <React.Fragment key={item.text}>
                {item.divider && <Divider sx={{ my: 1 }} />}
                <MenuItem
                  onClick={item.action}
                  sx={{
                    py: 1.5,
                    px: 3,
                    gap: 2,
                    '&:hover': {
                      backgroundColor: item.color + '15',
                      '& .MuiListItemIcon-root': {
                        color: item.color}},
                    transition: 'all 0.2s ease-in-out'}}
                >
                  <ListItemIcon sx={{ minWidth: 'auto', color: theme.palette.text.secondary }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: 500}}
                  />
                </MenuItem>
              </React.Fragment>
            ))}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default LoggedInProfileMenuComponent;
